import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, Orbit, MousePointer, Focus, Plane, Box, Grid3X3 } from 'lucide-react';
import { useTooltip } from './Tooltip';
import { useTranslation } from 'react-i18next';

export default function CameraControls({ viewer }) {
    const { t } = useTranslation();
    // State to track the current navigation mode (orbit or fps)
    const [navMode, setNavMode] = useState('orbit');
    // State to track if the camera is in orthographic mode
    const [isOrtho, setIsOrtho] = useState(false);

    const { showTooltip, hideTooltip } = useTooltip();

    // This effect runs once to set the initial state from the viewer
    useEffect(() => {
        if (!viewer) return;

        // Set initial camera projection mode
        const initialMode = viewer.scene.cameraMode;
        setIsOrtho(initialMode === window.Potree.CameraMode.ORTHOGRAPHIC);

        // Add an event listener to keep our state in sync if the mode changes elsewhere
        const onCameraModeChange = (e) => setIsOrtho(e.mode === window.Potree.CameraMode.ORTHOGRAPHIC);
        viewer.scene.addEventListener('cameramode_changed', onCameraModeChange);

        // Cleanup the event listener when the component unmounts
        return () => viewer.scene.removeEventListener('cameramode_changed', onCameraModeChange);

    }, [viewer]);

    // Handler for toggling camera projection
    const handleProjectionToggle = () => {
        // Determine the new mode based on our current React state
        const newMode = isOrtho
            ? window.Potree.CameraMode.PERSPECTIVE
            : window.Potree.CameraMode.ORTHOGRAPHIC;

        // Call the Potree API
        viewer.setCameraMode(newMode);

        // Update our React state
        setIsOrtho(!isOrtho);
    };

    // Handler for toggling navigation controls
    const handleNavToggle = () => {
        if (navMode === 'orbit') {
            viewer.setControls(viewer.fpControls);
            setNavMode('fps');
        } else {
            viewer.setControls(viewer.orbitControls);
            viewer.fitToScreen();
            setNavMode('orbit');
        }
    };

    // Handler for the "Focus" or "Fit to Screen" action
    const handleFocus = () => {
        viewer.fitToScreen();
    };

    return (
        <motion.div
            className="camera-controls-toolbar"
            initial={{ y: 50, x: "-50%", opacity: 0 }}
            animate={{ y: 0, x: "-50%", opacity: 1 }}
            transition={{ duration: 0.1, ease: "easeOut", delay: 0.2 }}
        >
            <motion.button
                className={`toolbar-button ${isOrtho ? 'active' : ''}`}
                onClick={handleProjectionToggle}
                whileTap={{ scale: 0.8 }}
                onMouseEnter={(e) => showTooltip(isOrtho ? t("cameraControls.tooltips.perspective") : t("cameraControls.tooltips.orthographic"), e)}
                onMouseLeave={hideTooltip}
            >
                <Grid3X3 size={22} className={!isOrtho ? 'icon-perspective' : ''} />
            </motion.button>

            <motion.button
                className="toolbar-button"
                onClick={handleNavToggle}
                whileTap={{ scale: 0.8 }}
                onMouseEnter={(e) => showTooltip(navMode === 'orbit' ? t("cameraControls.tooltips.fps") : t("cameraControls.tooltips.orbit"), e)}
                onMouseLeave={hideTooltip}
            >
                {/* Conditionally render the icon based on the current mode */}
                {navMode === 'orbit' ? <Orbit size={22} /> : <Plane size={22} />}
            </motion.button>

            <motion.button
                className="toolbar-button"
                onClick={handleFocus}
                whileTap={{ scale: 0.8 }}
                onMouseEnter={(e) => showTooltip(t("cameraControls.tooltips.focus"), e)}
                onMouseLeave={hideTooltip}
            >
                <Focus size={22} />
            </motion.button>
        </motion.div>
    );
}